// AdminAppAcademyNewPage — Rich course generator.
// Flow: title + category + theme + language →
//       source PDFs (uploaded to RAG) + URLs →
//       linked Atlas fiches (appellations · cépages · producteurs) →
//       optional extra instructions → generate.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, AlertCircle, Upload, FileText, X,
  MapPin, Grape, Building2, Link2, Trash2, Layers, Brain, ClipboardCheck, Zap,
  Wand2, ShoppingBag, Frame,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import {
  suggestLinkedFiches,
  suggestShopProduct,
  buildStructurePreamble,
  buildRegionIframeUrl,
} from "@/lib/academyComposer";

const CATEGORIES = [
  { key: "regions", label: "Régions" },
  { key: "cepages", label: "Cépages" },
  { key: "vinification", label: "Vinification & techniques" },
  { key: "degustation", label: "Dégustation" },
  { key: "terroir", label: "Terroir & géologie" },
  { key: "business", label: "Business & marché" },
  { key: "histoire", label: "Histoire & culture" },
  { key: "autre", label: "Autre" },
];

const AdminAppAcademyNewPage = () => {
  const nav = useNavigate();

  // Section 1 — base info
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("regions");
  const [theme, setTheme] = useState("");
  const [language, setLanguage] = useState("fr");

  // Section 2 — sources
  const [uploads, setUploads] = useState([]); // [{file_name, doc_id, size, uploading, error}]
  const [urls, setUrls] = useState("");

  // Section 3 — linked Atlas fiches
  const [atlas, setAtlas] = useState({ appellations: [], cepages: [], producers: [] });
  const [atlasLoading, setAtlasLoading] = useState(true);
  const [linkedApp, setLinkedApp] = useState(new Set());
  const [linkedCep, setLinkedCep] = useState(new Set());
  const [linkedProd, setLinkedProd] = useState(new Set());
  const [atlasQuery, setAtlasQuery] = useState("");

  // Section 4 — extra
  const [extra, setExtra] = useState("");

  // Section 3.6 — Shop product + region iframe (Phase 6 · course wrap-up).
  // Both are auto-suggested from the title/theme + linked appellations
  // and can be overridden by the operator via dropdown / free-text.
  const [shopProducts, setShopProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [iframeUrl, setIframeUrl] = useState("");
  const [autoMapEnabled, setAutoMapEnabled] = useState(true);
  const lastAutoSeedRef = useRef(""); // debounce guard against re-suggesting same seed

  // Section 3.5 — Individual Atlas blocks composer (Phase 4).
  // Loaded lazily when a fiche is toggled on; keyed by `${entity}:${slug}`.
  // blocksByFiche = { "appellation:chablis": [{id, category, title, content, ...}] }
  const [blocksByFiche, setBlocksByFiche] = useState({});
  const [blocksLoading, setBlocksLoading] = useState({});
  const [selectedBlockIds, setSelectedBlockIds] = useState(new Set());
  const [includeFlashcards, setIncludeFlashcards] = useState(true);
  const [includeQuizzes, setIncludeQuizzes] = useState(true);
  const [includeMicrolearning, setIncludeMicrolearning] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadAtlas = useCallback(async () => {
    setAtlasLoading(true);
    try {
      const [a, c, p] = await Promise.all([
        adminApi.listAppAppellations().catch(() => []),
        adminApi.listAppCepages().catch(() => []),
        adminApi.listAppProducers().catch(() => []),
      ]);
      const asList = (x) => Array.isArray(x) ? x : x?.items || x?.appellations || x?.cepages || x?.producers || [];
      setAtlas({ appellations: asList(a), cepages: asList(c), producers: asList(p) });
    } finally {
      setAtlasLoading(false);
    }
  }, []);
  useEffect(() => { loadAtlas(); }, [loadAtlas]);

  // Load shop products once (used for the certification block CTA).
  useEffect(() => {
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/shop/products?limit=200`);
        const data = await r.json();
        const items = Array.isArray(data) ? data : (data?.items || []);
        setShopProducts(items);
      } catch { /* non-blocking — dropdown just stays empty */ }
    })();
  }, []);

  // Auto-suggest linked fiches + shop product + iframe URL from the
  // title/theme text. Debounced by comparing against `lastAutoSeedRef`
  // so re-render loops don't re-suggest the same seed. Runs only when
  // `autoMapEnabled` is on so the operator can disable auto-mapping.
  useEffect(() => {
    if (!autoMapEnabled) return;
    if (atlasLoading) return;
    const seed = `${title} ${theme}`.trim();
    if (!seed || seed === lastAutoSeedRef.current) return;
    if (seed.length < 4) return;
    lastAutoSeedRef.current = seed;
    const suggested = suggestLinkedFiches({ seed, atlas });
    // Merge (don't overwrite) so operator picks aren't lost.
    setLinkedApp((prev) => new Set([...prev, ...suggested.appellations]));
    setLinkedCep((prev) => new Set([...prev, ...suggested.cepages]));
    setLinkedProd((prev) => new Set([...prev, ...suggested.producers]));
    // Shop product
    if (!selectedProductId && shopProducts.length) {
      const pid = suggestShopProduct({
        seed,
        atlas,
        linkedAppellations: Array.from(suggested.appellations),
        products: shopProducts,
      });
      if (pid) setSelectedProductId(pid);
    }
    // Iframe URL — derive from the first linked appellation's region.
    if (!iframeUrl && suggested.appellations.size) {
      const firstSlug = Array.from(suggested.appellations)[0];
      const app = (atlas.appellations || []).find((a) => a.slug === firstSlug);
      const regionSlug = (app?.region || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (regionSlug) setIframeUrl(buildRegionIframeUrl(regionSlug));
    }
  }, [title, theme, atlas, atlasLoading, autoMapEnabled, shopProducts]);

  // Manual "re-suggest" — resets the debounce guard and re-runs auto-map.
  const triggerAutoMap = () => {
    lastAutoSeedRef.current = "";
    const seed = `${title} ${theme}`.trim();
    if (!seed) return;
    const suggested = suggestLinkedFiches({ seed, atlas });
    setLinkedApp((prev) => new Set([...prev, ...suggested.appellations]));
    setLinkedCep((prev) => new Set([...prev, ...suggested.cepages]));
    setLinkedProd((prev) => new Set([...prev, ...suggested.producers]));
    if (shopProducts.length) {
      const pid = suggestShopProduct({
        seed, atlas,
        linkedAppellations: Array.from(new Set([...linkedApp, ...suggested.appellations])),
        products: shopProducts,
      });
      if (pid) setSelectedProductId(pid);
    }
    if (suggested.appellations.size) {
      const firstSlug = Array.from(suggested.appellations)[0];
      const app = (atlas.appellations || []).find((a) => a.slug === firstSlug);
      const regionSlug = (app?.region || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      if (regionSlug) setIframeUrl(buildRegionIframeUrl(regionSlug));
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    for (const file of list) {
      // Cap at 15 MB (bridge enforces same).
      if (file.size > 15 * 1024 * 1024) {
        setUploads((u) => [...u, { file_name: file.name, size: file.size, error: "> 15 MB — refusé" }]);
        continue;
      }
      const placeholder = { file_name: file.name, size: file.size, uploading: true };
      setUploads((u) => [...u, placeholder]);
      try {
        const res = await adminApi.ragUpload(file);
        const doc_id = res?.doc_id || res?.id || res?._id;
        setUploads((u) => u.map((x) =>
          x === placeholder ? { file_name: file.name, size: file.size, doc_id, uploading: false } : x
        ));
      } catch (e) {
        setUploads((u) => u.map((x) =>
          x === placeholder ? { file_name: file.name, size: file.size, uploading: false, error: e?.message || "Échec" } : x
        ));
      }
    }
  };

  const removeUpload = (idx) => setUploads((u) => u.filter((_, i) => i !== idx));

  const toggleLink = (setFn, slug) => {
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const filteredAppellations = useMemo(() => {
    const q = atlasQuery.trim().toLowerCase();
    if (!q) return atlas.appellations;
    return atlas.appellations.filter((a) =>
      (a.name || "").toLowerCase().includes(q) || (a.region || "").toLowerCase().includes(q)
    );
  }, [atlas.appellations, atlasQuery]);
  const filteredCepages = useMemo(() => {
    const q = atlasQuery.trim().toLowerCase();
    if (!q) return atlas.cepages;
    return atlas.cepages.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [atlas.cepages, atlasQuery]);
  const filteredProducers = useMemo(() => {
    const q = atlasQuery.trim().toLowerCase();
    if (!q) return atlas.producers;
    return atlas.producers.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [atlas.producers, atlasQuery]);

  // ── Phase 4 — load individual Atlas blocks for every linked fiche ────
  useEffect(() => {
    const wanted = [
      ...Array.from(linkedApp).map((s) => ["appellation", s]),
      ...Array.from(linkedCep).map((s) => ["cepage", s]),
      ...Array.from(linkedProd).map((s) => ["producer", s]),
    ];
    wanted.forEach(([et, s]) => {
      const key = `${et}:${s}`;
      if (blocksByFiche[key] || blocksLoading[key]) return;
      setBlocksLoading((L) => ({ ...L, [key]: true }));
      adminApi.listAtlasBlocks(et, s).then((res) => {
        const list = Array.isArray(res) ? res : (res?.blocks || res?.items || []);
        setBlocksByFiche((B) => ({ ...B, [key]: list }));
      }).catch(() => {
        setBlocksByFiche((B) => ({ ...B, [key]: [] }));
      }).finally(() => {
        setBlocksLoading((L) => ({ ...L, [key]: false }));
      });
    });
    // Drop deselected fiches from state so their blocks vanish from the UI.
    const wantedKeys = new Set(wanted.map(([et, s]) => `${et}:${s}`));
    setSelectedBlockIds((prev) => {
      let mutated = false;
      const next = new Set(prev);
      // Recompute selectedBlockIds from currently loaded blocks: drop any
      // block whose parent fiche is no longer linked.
      Object.entries(blocksByFiche).forEach(([key, blocks]) => {
        if (!wantedKeys.has(key)) {
          (blocks || []).forEach((b) => {
            if (b?.id && next.has(b.id)) { next.delete(b.id); mutated = true; }
          });
        }
      });
      return mutated ? next : prev;
    });
  }, [linkedApp, linkedCep, linkedProd]);

  const toggleBlock = (blockId) => {
    setSelectedBlockIds((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const flatAtlasBlocks = useMemo(() => {
    const items = [];
    const linkedKeys = new Set([
      ...Array.from(linkedApp).map((s) => `appellation:${s}`),
      ...Array.from(linkedCep).map((s) => `cepage:${s}`),
      ...Array.from(linkedProd).map((s) => `producer:${s}`),
    ]);
    Object.entries(blocksByFiche).forEach(([key, blocks]) => {
      if (!linkedKeys.has(key)) return;
      const [et, slug] = key.split(":");
      (blocks || []).forEach((b) => {
        if (!b?.id) return;
        items.push({ ...b, _entity: et, _slug: slug });
      });
    });
    return items;
  }, [blocksByFiche, linkedApp, linkedCep, linkedProd]);

  const handleGenerate = async () => {
    setError(null);
    if (!title.trim()) { setError("Titre requis"); return; }
    if (!theme.trim()) { setError("Merci de préciser le thème / angle du parcours"); return; }
    const source_doc_ids = uploads.filter((u) => u.doc_id).map((u) => u.doc_id);
    const source_urls = urls.split("\n").map((s) => s.trim()).filter(Boolean);

    // Phase 4 — inject the selected Atlas blocks + composition toggles into
    // extra_instructions so the upstream generator prioritises them without
    // requiring an upstream schema change.
    const chosen = flatAtlasBlocks.filter((b) => selectedBlockIds.has(b.id));

    // Resolve names for the structure preamble so the LLM prompt embeds
    // the actual fiche + product names (not just slugs).
    const nameByAppSlug = new Map((atlas.appellations || []).map((a) => [a.slug, a.name]));
    const nameByCepSlug = new Map((atlas.cepages || []).map((c) => [c.slug, c.name]));
    const nameByProdSlug = new Map((atlas.producers || []).map((p) => [p.slug, p.name]));
    const shopProduct = shopProducts.find((p) => (p.product_id || p.id) === selectedProductId) || null;
    const firstApp = (atlas.appellations || []).find((a) => linkedApp.has(a.slug));
    const regionLabel = firstApp?.region || "";
    const regionSlug = regionLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    // Mandatory 10-section structure preamble → forces the upstream LLM
    // to produce a rich Geovinum-branded parcours instead of the current
    // 3-block skeletons.
    const structurePreamble = buildStructurePreamble({
      regionSlug,
      regionLabel,
      linkedAppellationNames: Array.from(linkedApp).map((s) => nameByAppSlug.get(s)).filter(Boolean),
      linkedCepageNames: Array.from(linkedCep).map((s) => nameByCepSlug.get(s)).filter(Boolean),
      linkedProducerNames: Array.from(linkedProd).map((s) => nameByProdSlug.get(s)).filter(Boolean),
      shopProduct,
      iframeUrl,
    });

    const composerPreamble = [structurePreamble];
    if (chosen.length) {
      composerPreamble.push(
        `\n\n=== BLOCS ATLAS PRIORITAIRES (${chosen.length}) ===`,
        "Utilise ces blocs comme MATIÈRE PRINCIPALE du parcours. Reprends leurs contenus, structure et exemples. Complète avec les sources jointes si besoin :"
      );
      chosen.forEach((b) => {
        composerPreamble.push(
          `\n• [${b._entity}:${b._slug}] [${b.category || "?"}] ${b.title || "(sans titre)"}`,
          `${(b.content || b.body || "").slice(0, 1500)}`
        );
      });
    }
    const modules = [];
    if (includeFlashcards) modules.push("- Produire 10-15 FLASHCARDS thématiques (recto question courte / verso réponse détaillée).");
    if (includeQuizzes) modules.push("- Produire un QUIZ final de 8-12 questions (QCM 4 choix + 1 seule bonne réponse, avec justification).");
    if (includeMicrolearning) modules.push("- Produire 5-8 CARTES MICROLEARNING autonomes (utilisables en push notification).");
    if (modules.length) {
      composerPreamble.push("\n=== MODULES OBLIGATOIRES À GÉNÉRER ===", ...modules);
    }

    const enrichedExtra = [extra.trim(), composerPreamble.join("\n")].filter(Boolean).join("\n");

    const payload = {
      title: title.trim(),
      category,
      theme: theme.trim(),
      language,
      linked_appellations: Array.from(linkedApp),
      linked_cepages: Array.from(linkedCep),
      linked_producers: Array.from(linkedProd),
      source_urls,
      source_doc_ids,
      extra_instructions: enrichedExtra,
      // Also send structured fields — upstream may or may not use them but
      // they're harmless if ignored.
      selected_block_ids: Array.from(selectedBlockIds),
      include_flashcards: includeFlashcards,
      include_quizzes: includeQuizzes,
      include_microlearning: includeMicrolearning,
      // Phase 6 — pass-through to upstream. Even if upstream ignores
      // these fields today, the structure preamble above already tells
      // the LLM to render them; keeping them here future-proofs the
      // integration when the upstream schema is updated.
      shop_product_id: selectedProductId || null,
      region_iframe_url: iframeUrl || null,
      region_slug: regionSlug || null,
      region_label: regionLabel || null,
    };
    setBusy(true);
    try {
      const res = await adminApi.generateAcademyRich(payload);
      const id = res?.id || res?._id || res?.course_id;
      if (id) nav(`/admin/app-academy/edit/${id}`);
      else setError("Réponse inattendue — aucun id retourné.");
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const formatBytes = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div data-testid="admin-app-academy-new">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Link to="/admin/app-academy" className="text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" data-testid="academy-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Nouveau parcours</h1>
        </div>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">L&apos;IA génère un parcours pédagogique à partir du titre, du thème, des sources fournies (PDF + URLs) et des fiches Atlas liées.</p>

      {/* Section 1 — Base info */}
      <Section title="1. Informations de base" testid="academy-section-base">
        <Field label="Titre du parcours *">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex : Le Chablis dans tous ses états"
            className="gv-input w-full"
            data-testid="academy-title-input"
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Catégorie">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="gv-input w-full" data-testid="academy-category-select">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Langue de génération">
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="gv-input w-full" data-testid="academy-language-select">
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="pt">Português</option>
            </select>
          </Field>
        </div>
        <Field label="Thème / angle du parcours *">
          <textarea
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            rows={3}
            placeholder="Ex : Un tour d'horizon complet du Chablis, de sa géologie kimméridgienne à ses 4 niveaux d'appellation, en insistant sur la minéralité comme fil rouge."
            className="gv-input w-full resize-none"
            data-testid="academy-theme-input"
          />
        </Field>
      </Section>

      {/* Section 2 — Sources */}
      <Section title="2. Sources de lecture pour l'IA" testid="academy-section-sources">
        <Field label="Documents PDF (max 15 MB / fichier)">
          <label
            className="block border-2 border-dashed border-[color:var(--gv-line)] rounded-[5px] p-6 text-center cursor-pointer hover:border-[color:var(--gv-pink)] transition-colors"
            data-testid="academy-pdf-dropzone"
          >
            <input
              type="file"
              multiple
              accept=".pdf,.txt,.md,.docx"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="academy-pdf-input"
            />
            <Upload size={18} className="mx-auto mb-2 text-[color:var(--gv-ink)]/40" />
            <div className="text-[13px] text-[color:var(--gv-ink)]/70">Cliquer pour uploader ou glisser-déposer</div>
            <div className="text-[11.5px] text-[color:var(--gv-ink)]/45 mt-0.5">PDF, TXT, MD, DOCX — plusieurs fichiers acceptés</div>
          </label>
          {uploads.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="academy-uploads-list">
              {uploads.map((u, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 bg-white border border-[color:var(--gv-line)] rounded-[4px] text-[13px]">
                  <FileText size={13} className="text-[color:var(--gv-ink)]/50 shrink-0" />
                  <span className="flex-1 truncate">{u.file_name}</span>
                  <span className="text-[11.5px] text-[color:var(--gv-ink)]/50 shrink-0">{formatBytes(u.size)}</span>
                  {u.uploading ? (
                    <Loader2 size={12} className="animate-spin text-[color:var(--gv-pink)]" />
                  ) : u.error ? (
                    <span className="text-[11px] text-red-600 shrink-0" title={u.error}>✗ {u.error}</span>
                  ) : (
                    <span className="text-[11px] text-emerald-600 shrink-0">✓ prêt</span>
                  )}
                  <button
                    onClick={() => removeUpload(i)}
                    className="p-0.5 rounded hover:bg-red-50 text-[color:var(--gv-ink)]/40 hover:text-red-600"
                    title="Retirer"
                    data-testid={`academy-upload-remove-${i}`}
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Field>

        <Field label="URLs à scraper (une par ligne)">
          <textarea
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={3}
            placeholder="https://…&#10;https://…"
            className="gv-input w-full resize-none font-mono text-[12.5px]"
            data-testid="academy-urls-input"
          />
        </Field>
      </Section>

      {/* Section 3 — Linked Atlas fiches */}
      <Section title="3. Fiches Atlas à lier" testid="academy-section-links">
        <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-3">
          L&apos;IA utilisera le contenu des fiches sélectionnées comme contexte enrichi (description, terroir, cépages, régulation…).
        </p>
        <div className="flex items-center gap-2 mb-3">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-[color:var(--gv-ink)]/75 cursor-pointer" data-testid="academy-automap-toggle">
            <input
              type="checkbox"
              checked={autoMapEnabled}
              onChange={(e) => setAutoMapEnabled(e.target.checked)}
              className="accent-[color:var(--gv-pink)]"
            />
            <Wand2 size={11} /> Suggérer automatiquement les fiches liées depuis le titre / thème
          </label>
          <button
            type="button"
            onClick={triggerAutoMap}
            disabled={!title.trim() && !theme.trim()}
            className="text-[11.5px] px-2 py-1 rounded border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] disabled:opacity-40"
            data-testid="academy-automap-button"
          >
            Suggérer maintenant
          </button>
        </div>
        <input
          value={atlasQuery}
          onChange={(e) => setAtlasQuery(e.target.value)}
          placeholder="Filtrer les fiches par nom ou région…"
          className="gv-input w-full mb-3"
          data-testid="academy-atlas-filter"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <AtlasPicker
            Icon={MapPin}
            label="Appellations"
            items={filteredAppellations}
            selected={linkedApp}
            onToggle={(slug) => toggleLink(setLinkedApp, slug)}
            testid="academy-picker-appellations"
            loading={atlasLoading}
          />
          <AtlasPicker
            Icon={Grape}
            label="Cépages"
            items={filteredCepages}
            selected={linkedCep}
            onToggle={(slug) => toggleLink(setLinkedCep, slug)}
            testid="academy-picker-cepages"
            loading={atlasLoading}
          />
          <AtlasPicker
            Icon={Building2}
            label="Producteurs"
            items={filteredProducers}
            selected={linkedProd}
            onToggle={(slug) => toggleLink(setLinkedProd, slug)}
            testid="academy-picker-producers"
            loading={atlasLoading}
          />
        </div>
      </Section>

      {/* Section 3.5 — Composer: pick individual blocks + modules ─────── */}
      {(linkedApp.size + linkedCep.size + linkedProd.size) > 0 && (
        <Section title="3.5 Composer le parcours (Phase 4)" testid="academy-section-composer">
          <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-3">
            Sélectionnez les <strong>blocs Atlas précis</strong> à réutiliser comme matière première et cochez les modules à générer en plus du parcours principal.
          </p>

          {/* Modules toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            <ModuleToggle
              Icon={Brain}
              label="Flashcards (10-15)"
              hint="Recto/verso, mémorisation active"
              checked={includeFlashcards}
              onChange={setIncludeFlashcards}
              testid="composer-toggle-flashcards"
            />
            <ModuleToggle
              Icon={ClipboardCheck}
              label="Quiz final (8-12 Q)"
              hint="QCM avec justification"
              checked={includeQuizzes}
              onChange={setIncludeQuizzes}
              testid="composer-toggle-quizzes"
            />
            <ModuleToggle
              Icon={Zap}
              label="Cartes microlearning (5-8)"
              hint="Push notifications autonomes"
              checked={includeMicrolearning}
              onChange={setIncludeMicrolearning}
              testid="composer-toggle-microlearning"
            />
          </div>

          {/* Blocks selection */}
          <div className="border border-[color:var(--gv-line)] rounded-[4px] bg-white overflow-hidden" data-testid="composer-blocks-picker">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--gv-ink)]">
                <Layers size={12} /> Blocs Atlas à intégrer
              </div>
              <span className="text-[11px] text-[color:var(--gv-ink)]/50">
                {selectedBlockIds.size}/{flatAtlasBlocks.length} sélectionnés
              </span>
            </div>
            {Object.values(blocksLoading).some(Boolean) && flatAtlasBlocks.length === 0 ? (
              <div className="p-4 text-[12px] text-[color:var(--gv-ink)]/50 flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Chargement des blocs des fiches sélectionnées…
              </div>
            ) : flatAtlasBlocks.length === 0 ? (
              <div className="p-4 text-[12px] text-[color:var(--gv-ink)]/50">
                Aucun bloc trouvé — les fiches sélectionnées n&apos;ont pas encore de contenu Atlas.
              </div>
            ) : (
              <div className="max-h-72 overflow-auto divide-y divide-[color:var(--gv-line)]/60">
                {flatAtlasBlocks.map((b) => {
                  const active = selectedBlockIds.has(b.id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => toggleBlock(b.id)}
                      className={`w-full text-left px-3 py-2 flex items-start gap-2 text-[12.5px] transition-colors ${
                        active ? "bg-[color:var(--gv-pink)]/10" : "hover:bg-[color:var(--gv-mist)]/50"
                      }`}
                      data-testid={`composer-block-${b.id}`}
                    >
                      <span className={`mt-0.5 w-3 h-3 rounded-sm border shrink-0 ${active ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)]" : "border-[color:var(--gv-line)]"}`}>
                        {active && <span className="block text-white text-[9px] leading-3 text-center">✓</span>}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]">{b._entity} · {b._slug}</span>
                          <span className="text-[10px] text-[color:var(--gv-ink)]/45">{b.category || "?"}</span>
                        </div>
                        <div className={`text-[13px] font-medium ${active ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"} truncate`}>
                          {b.title || "(sans titre)"}
                        </div>
                        <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 line-clamp-1">
                          {(b.content || b.body || "").slice(0, 160).replace(/\n/g, " ")}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick actions */}
          {flatAtlasBlocks.length > 0 && (
            <div className="flex items-center gap-2 mt-2 text-[11.5px]">
              <button
                type="button"
                onClick={() => setSelectedBlockIds(new Set(flatAtlasBlocks.map((b) => b.id)))}
                className="text-[color:var(--gv-pink)] hover:underline"
                data-testid="composer-select-all"
              >
                Tout sélectionner
              </button>
              <span className="text-[color:var(--gv-ink)]/30">·</span>
              <button
                type="button"
                onClick={() => setSelectedBlockIds(new Set())}
                className="text-[color:var(--gv-ink)]/60 hover:underline"
                data-testid="composer-clear-all"
              >
                Tout désélectionner
              </button>
            </div>
          )}
        </Section>
      )}
      <Section title="4. Certification & carte à offrir" testid="academy-section-shop">
        <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mb-3">
          Auto-détecté depuis la région ; modifiable à tout moment. Utilisé pour générer le bloc <em>Certification</em> avec le CTA « Achetez la carte » à la fin du parcours et l&apos;<em>iframe</em> interactive dans le bloc géographie.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Produit boutique associé (fin de parcours)">
            <div className="flex items-center gap-2">
              <ShoppingBag size={13} className="text-[color:var(--gv-ink)]/40 shrink-0" />
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="gv-input w-full text-[12.5px]"
                data-testid="academy-shop-product-select"
              >
                <option value="">— aucun / placeholder « voir catalogue »</option>
                {shopProducts.map((p) => {
                  const pid = p.product_id || p.id;
                  return (
                    <option key={pid} value={pid}>
                      {(p.region_label ? `[${p.region_label}] ` : "") + (p.title || pid).slice(0, 90)}
                    </option>
                  );
                })}
              </select>
            </div>
          </Field>
          <Field label="URL iframe carte de la région">
            <div className="flex items-center gap-2">
              <Frame size={13} className="text-[color:var(--gv-ink)]/40 shrink-0" />
              <input
                value={iframeUrl}
                onChange={(e) => setIframeUrl(e.target.value)}
                placeholder="https://geovinum.io/embed/region/beaujolais"
                className="gv-input w-full text-[12.5px] font-mono"
                data-testid="academy-iframe-url-input"
              />
            </div>
          </Field>
        </div>
      </Section>

      <Section title="5. Instructions complémentaires" testid="academy-section-extra">
        <textarea
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          rows={3}
          placeholder="Ex : Cibler niveau WSET Level 2. Ajouter un quiz final. Ton pédagogique et accessible…"
          className="gv-input w-full resize-none"
          data-testid="academy-extra-input"
        />
      </Section>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="academy-new-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 self-center">
          {uploads.filter((u) => u.doc_id).length} PDF · {urls.split("\n").filter((s) => s.trim()).length} URLs · {linkedApp.size + linkedCep.size + linkedProd.size} fiches liées
        </div>
        <button
          onClick={handleGenerate}
          disabled={busy || uploads.some((u) => u.uploading)}
          className="inline-flex items-center gap-1.5 px-5 py-3 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[14px] hover:bg-[color:var(--gv-pink-dark,#C01661)] disabled:opacity-50"
          data-testid="academy-generate-submit"
        >
          {busy ? <><Loader2 size={13} className="animate-spin" /> Génération 60-120 s…</> : <><Sparkles size={13} /> Générer le parcours</>}
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 mb-4" data-testid={testid}>
    <h3 className="font-display text-[15px] text-[color:var(--gv-ink)] mb-4">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{label}</label>
    {children}
  </div>
);

const AtlasPicker = ({ Icon, label, items, selected, onToggle, testid, loading }) => (
  <div className="border border-[color:var(--gv-line)] rounded-[4px] bg-white" data-testid={testid}>    <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/40">
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--gv-ink)]">
        <Icon size={12} /> {label}
      </div>
      <span className="text-[11px] text-[color:var(--gv-ink)]/50">{selected.size}/{items.length}</span>
    </div>
    <div className="max-h-64 overflow-auto p-1">
      {loading ? (
        <div className="p-3 text-[12px] text-[color:var(--gv-ink)]/50 flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="p-3 text-[12px] text-[color:var(--gv-ink)]/40">Aucune fiche</div>
      ) : items.map((it) => {
        const slug = it.slug || it.id;
        if (!slug) return null;
        const active = selected.has(slug);
        return (
          <button
            key={slug}
            onClick={() => onToggle(slug)}
            className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-[12.5px] transition-colors ${
              active ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)] hover:bg-[color:var(--gv-mist)]/50"
            }`}
            data-testid={`${testid}-item-${slug}`}
          >
            <span className={`w-3 h-3 rounded-sm border ${active ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)]" : "border-[color:var(--gv-line)]"}`}>
              {active && <span className="block text-white text-[9px] leading-3 text-center">✓</span>}
            </span>
            <span className="flex-1 truncate">{it.name}</span>
            {it.region && <span className="text-[10.5px] text-[color:var(--gv-ink)]/45 truncate">{it.region}</span>}
          </button>
        );
      })}
    </div>
  </div>
);

const ModuleToggle = ({ Icon, label, hint, checked, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`text-left border rounded-[4px] px-3 py-2.5 transition-colors ${
      checked
        ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5"
        : "border-[color:var(--gv-line)] bg-white hover:border-[color:var(--gv-ink)]/20"
    }`}
    data-testid={testid}
    aria-pressed={checked}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className={`w-3.5 h-3.5 rounded-sm border ${checked ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)]" : "border-[color:var(--gv-line)]"}`}>
        {checked && <span className="block text-white text-[9px] leading-3 text-center">✓</span>}
      </span>
      <Icon size={13} className={checked ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/60"} />
      <span className={`text-[12.5px] font-medium ${checked ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"}`}>{label}</span>
    </div>
    <div className="text-[10.5px] text-[color:var(--gv-ink)]/55 pl-5">{hint}</div>
  </button>
);

export default AdminAppAcademyNewPage;
